import { Link, useLocation } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { ArrowRight } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

/**
 * MobileStickyCta — mint-pill CTA fixed to the bottom of the viewport on
 * mobile only. Replaces the in-header "Start Building" button (hidden
 * `hidden sm:inline-flex` on phones for nav-bar room) and guarantees a
 * one-tap path to conversion no matter where the teacher is on the page.
 *
 * Routing rule mirrors `<StartBuildingButton />`:
 *   • signed-in user with an active plan / admin → /generator
 *   • everyone else → /pricing
 *
 * Pages where we hide the bar to avoid visual clutter (for ANY visitor):
 *   • / (homepage — Phase 137 restructure replaced this with a hero CTA
 *     + a closing "Ready when you are" trial CTA; sticky competes with both)
 *   • /generator and /skills (the teacher is already in/at the destination)
 *   • /pricing, /billing/success (form pages — sticky CTA would compete)
 *   • signed-in pages already showing a primary action (/dashboard, /admin,
 *     /math-engine, /the-path-of-the-app, account, etc.)
 *
 * Founder rule (Jul 12 2026): the bar never renders for a teacher who
 * has already signed up for a paid plan or free trial. Anyone with an
 * active-shaped subscription status (`trialing`, `active`, `past_due`)
 * OR admin standing sees no floating pitch anywhere in the app — they
 * already committed, the pitch is noise. Definition matches
 * `_isProUser` in `TodayCard.jsx` so there's one source of truth in
 * the codebase.
 *
 * Padding-bottom helper: we add a sibling spacer so page content never
 * gets hidden behind the bar (h-16 == the bar's height).
 */
const HIDE_ON = new Set([
  "/",
  "/generator",
  "/skills",
  "/pricing",
  "/billing/success",
  "/dashboard",
  "/admin",
  "/math-engine",
  "/account",
  "/my-lesson",
  "/lesson",
  "/free-lesson",
  "/links",
  "/samples",
]);

// Founder rule (Jul 12 2026): "anyone that has signed up for a paid
// plan, free trial or already paid" — no floating pitch anywhere in
// the app. We defer to the backend-computed `subscription.is_active`
// flag on `/api/auth/me` (see backend/routes/auth.py:126), which is
// `subscription_status ∈ {trialing, active, past_due, beta}`. That's
// one source of truth for "subscriber standing" across the codebase.
// Admins pass through as staff.
const _hasSubscriberStanding = (user) => {
  if (!user) return false;
  if (user.is_admin) return true;
  return Boolean(user?.subscription?.is_active);
};

export const MobileStickyCta = () => {
  const { user, loading } = useAuth();
  const { pathname } = useLocation();

  // Measure the bar's real rendered height (pill + padding + iOS bottom
  // safe-area inset, all baked into offsetHeight) so the sibling spacer
  // below never lets content hide behind it — no hardcoded height. Falls
  // back to 96px (the old h-24) before the first measure / on desktop
  // where the bar is `sm:hidden` (offsetHeight 0 → no spacer needed).
  const barRef = useRef(null);
  const [barHeight, setBarHeight] = useState(96);
  useEffect(() => {
    const el = barRef.current;
    if (!el) {
      setBarHeight(0);
      return undefined;
    }
    const measure = () => setBarHeight(el.offsetHeight || 96);
    measure();
    const ro =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    if (ro) ro.observe(el);
    window.addEventListener("resize", measure);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener("resize", measure);
    };
    // `loading` is a dep because the bar is not rendered while auth is
    // resolving (early `return null` below). When loading flips true→false
    // the bar mounts, but for an anonymous visitor neither `pathname` nor
    // `user` changes — without `loading` here the effect would not re-run,
    // `barRef.current` would still be the null it was during loading, and
    // `barHeight` would stay 0 (spacer collapses, content hides behind the
    // bar). Re-running on `loading` re-measures once the bar actually mounts.
  }, [pathname, user, loading]);

  if (loading) return null;
  // Path-based suppression (covers /skills, /skills/anything, /admin/X, etc).
  if ([...HIDE_ON].some((p) => pathname === p || pathname.startsWith(p + "/"))) {
    return null;
  }
  // Any subscribing / trialing / past-due / admin visitor: no pitch
  // anywhere. Freemium signed-in and anonymous visitors keep the CTA
  // on non-HIDE_ON surfaces so the pre-conversion story is intact.
  if (_hasSubscriberStanding(user)) {
    return null;
  }

  // Signed-in active users → route to the generator with the
  // shorter "Start Building" label (they already have an account, the
  // "free" framing is irrelevant). Everyone else → /pricing with the
  // "Start Building for free" hook.
  const isActiveUser =
    !!user && (user?.subscription?.is_active || user?.is_admin);
  const target = isActiveUser ? "/generator" : "/pricing";
  const label = isActiveUser ? "Start Building" : "Start Building for free";

  return (
    <>
      {/* Spacer so the bar never covers the last bit of page content.
          Its height is MEASURED from the bar (offsetHeight already includes
          pt-3 + the h-14 pill + pb-[max(1rem,env(safe-area-inset-bottom))]),
          so it self-corrects if the pill wraps or the iOS home-indicator
          inset changes — no hardcoded value. */}
      <div
        aria-hidden="true"
        className="sm:hidden"
        style={{ height: `${barHeight}px` }}
        data-testid="mobile-sticky-cta-spacer"
      />
      <Link
        ref={barRef}
        to={target}
        data-testid="mobile-sticky-cta"
        className="sm:hidden fixed inset-x-0 bottom-0 z-40 px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-3 bg-gradient-to-t from-[#1c2d3d]/95 via-[#1c2d3d]/85 to-[#1c2d3d]/0 backdrop-blur-sm"
      >
        <span className="flex w-full h-14 items-center justify-center gap-2 rounded-full bg-[#96ded1] text-[#1c2d3d] text-base font-extrabold shadow-2xl shadow-[#96ded1]/40 active:scale-[0.98] transition-transform">
          {label}
          <ArrowRight className="h-4 w-4" />
        </span>
      </Link>
    </>
  );
};

export default MobileStickyCta;
